// components/TradingCommand/Orders.jsx
import React from 'react';

// ─── BRD Design Tokens ────────────────────────────────────────────────────────
const T = {
  bg:     '#08101E',
  bg1:    '#0C1525',
  bg2:    '#101B2E',
  border: '#1E2D42',
  text:   '#E8EDF5',
  body:   '#B8C8DC',
  meta:   '#6A7D95',
  green:  '#00E896',
  red:    '#FF4466',
  amber:  '#FFB340',
  cyan:   '#00C8FF',
  purple: '#9966FF',
  font:   "'DM Mono', ui-monospace, monospace",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeOnly = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return [d.getHours(), d.getMinutes(), d.getSeconds()]
      .map(n => String(n).padStart(2, '0'))
      .join(':');
  } catch { return '—'; }
};

const fmtPrice = (val) =>
  val != null && parseFloat(val) !== 0
    ? `$${parseFloat(val).toFixed(2)}`
    : '—';

// Parse OCC option symbol e.g. SPY260206P00675000 → "SPY 675P 02/06"
const parseOptionSymbol = (sym) => {
  const m = String(sym || '').match(/^([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return null;
  const [, und, yy, mm, dd, cp, strike8] = m;
  const strike = (parseInt(strike8, 10) / 1000).toFixed(0);
  return { und, expiry: `${mm}/${dd}`, type: cp === 'C' ? 'CALL' : 'PUT', strike };
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    partially_filled: { label: 'PARTIAL', color: T.amber },
    new:              { label: 'PENDING', color: T.cyan  },
    held:             { label: 'HELD',    color: T.purple },
    replaced:         { label: 'REPLACED',color: T.meta  },
  }[status] || { label: status?.toUpperCase() || '—', color: T.meta };

  return (
    <span style={{
      fontFamily: T.font,
      fontSize: '9px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      color: cfg.color,
      backgroundColor: cfg.color + '18',
      border: `1px solid ${cfg.color}50`,
      borderRadius: '3px',
      padding: '2px 6px',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

// ─── Asset badge ──────────────────────────────────────────────────────────────
const AssetBadge = ({ assetClass }) => {
  const isOption = assetClass === 'us_option' || assetClass === 'option';
  return (
    <span style={{
      fontFamily: T.font,
      fontSize: '9px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      color: isOption ? T.purple : T.cyan,
      backgroundColor: (isOption ? T.purple : T.cyan) + '15',
      border: `1px solid ${(isOption ? T.purple : T.cyan)}40`,
      borderRadius: '3px',
      padding: '2px 5px',
    }}>
      {isOption ? 'OPT' : 'EQ'}
    </span>
  );
};

// ─── TH helper ────────────────────────────────────────────────────────────────
const Th = ({ children }) => (
  <th style={{
    fontFamily: T.font,
    fontSize: '9px',
    fontWeight: 500,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    color: T.meta,
    padding: '6px 10px',
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: 'nowrap',
    background: T.bg1,
  }}>
    {children}
  </th>
);

const Td = ({ children, color = T.body, align = 'left' }) => (
  <td style={{
    fontFamily: T.font,
    fontSize: '11px',
    color,
    padding: '8px 10px',
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: 'middle',
    textAlign: align,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </td>
);

// ─── Main Component ───────────────────────────────────────────────────────────
function Orders({ data = [] }) {
  // Unwrap { order: ... } wrappers — allOrders uses this shape
  const orders = data.map(item => item.order || item);

  // Deduplicate by id, prefer latest snapshot (higher filled_qty or newer timestamp)
  const byId = new Map();
  for (const o of orders) {
    if (!o?.id) continue;
    const prev = byId.get(o.id);
    if (!prev) { byId.set(o.id, o); continue; }
    const prevFilled = parseFloat(prev.filled_qty || 0);
    const curFilled  = parseFloat(o.filled_qty   || 0);
    const prevT = new Date(prev.updated_at || prev.submitted_at || 0).getTime();
    const curT  = new Date(o.updated_at   || o.submitted_at   || 0).getTime();
    if (curFilled > prevFilled || (curFilled === prevFilled && curT > prevT)) {
      byId.set(o.id, o);
    }
  }

  // Filter to only pending/in-flight orders (equity AND options)
  const pending = Array.from(byId.values()).filter(o =>
    o.status === 'new'
    || o.status === 'partially_filled'
    || (o.status === 'replaced' && parseFloat(o.filled_qty || 0) === 0)
    || (o.status === 'held'     && parseFloat(o.filled_qty || 0) === 0)
  );

  if (pending.length === 0) return null;

  // Sort: partially_filled first (most urgent), then by submitted_at desc
  pending.sort((a, b) => {
    if (a.status === 'partially_filled' && b.status !== 'partially_filled') return -1;
    if (b.status === 'partially_filled' && a.status !== 'partially_filled') return  1;
    return new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0);
  });

  return (
    <div style={{
      backgroundColor: T.bg1,
      border: `1px solid ${T.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <div style={{
          fontFamily: T.font,
          fontSize: '9px',
          fontWeight: 500,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: T.meta,
        }}>
          Open Orders
        </div>
        <span style={{
          fontFamily: T.font,
          fontSize: '10px',
          fontWeight: 600,
          color: T.amber,
          backgroundColor: T.amber + '18',
          border: `1px solid ${T.amber}40`,
          borderRadius: '3px',
          padding: '1px 7px',
        }}>
          {pending.length}
        </span>
      </div>

      {/* Table */}
      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>Time</Th>
              <Th>Type</Th>
              <Th>Symbol</Th>
              <Th>Side</Th>
              <Th>Order Type</Th>
              <Th>Status</Th>
              <Th>Qty</Th>
              <Th>Limit</Th>
              <Th>Stop</Th>
            </tr>
          </thead>
          <tbody>
            {pending.map((o) => {
              const isOption = o.asset_class === 'us_option' || o.asset_class === 'option';
              const opt = isOption ? parseOptionSymbol(o.symbol) : null;
              const isLong = (o.side || '').toLowerCase() === 'buy' || (o.side || '').includes('open');
              const sideColor = isLong ? T.green : T.red;

              // Display symbol: for options show parsed readable form
              const symbolDisplay = opt
                ? `${opt.und} ${opt.strike}${opt.type[0]} ${opt.expiry}`
                : (o.symbol || '—');

              const filledQty = parseFloat(o.filled_qty || 0);
              const totalQty  = parseFloat(o.qty || 0);
              const qtyDisplay = filledQty > 0
                ? `${filledQty}/${totalQty}`
                : String(totalQty);

              return (
                <tr
                  key={o.id}
                  style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Td color={T.meta}>{timeOnly(o.submitted_at)}</Td>
                  <Td><AssetBadge assetClass={o.asset_class} /></Td>
                  <Td color={T.text}>{symbolDisplay}</Td>
                  <Td>
                    <span style={{
                      fontFamily: T.font,
                      fontSize: '11px',
                      fontWeight: 600,
                      color: sideColor,
                      letterSpacing: '0.06em',
                    }}>
                      {(o.side || '').toUpperCase()}
                    </span>
                  </Td>
                  <Td color={T.body}>{(o.type || '').toUpperCase()}</Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td color={T.body} align="right">{qtyDisplay}</Td>
                  <Td color={T.cyan}  align="right">{fmtPrice(o.limit_price)}</Td>
                  <Td color={T.amber} align="right">{fmtPrice(o.stop_price)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Orders;